import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  Pipe,
  PipeTransform,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FloorcareProviderNames,
  ServiceOptionTypes,
} from '@domgen/dgx-components';
import { CONFIG } from '@domgen/dgx-fe-config';
import { isAfter, isToday, parse, subDays } from 'date-fns';
import { AppConfig } from '../../../types/app-config.model';
import {
  ClaimStatusTypeEnum,
  REMEDIAL_WORK_PHONE_NUMBER,
} from '../../../types/applianceTypeEnum';
import {
  MMRAppointmentHistory,
  MMRClaimDetail,
  ServiceOptionTypeEnum,
} from '../../../types/manage-my-repair-types';
import { ClaimType } from '../../../types/plan-detail.types';
import { consoleCodes } from '@domgen/dgx-util-general';
import posthog from 'posthog-js';

type TemplateContext = {
  item: MMRAppointmentHistory;
  last: boolean;
};

@Pipe({ name: 'bookedTimeDate' })
export class BookedTimeDatePipe implements PipeTransform {
  transform(bookedDate: string): string {
    if (!bookedDate) return '';
    let date: any = bookedDate.split(' ')[0];
    date = date.split(/[-/]/);

    if (date[0].length === 4) {
      date = date.reverse();
    }
    date[2] = date[2].substr(2, 4);
    return date.join('/');
  }
}

@Pipe({ name: 'bookedTime' })
export class BookedTimePipe implements PipeTransform {
  transform(bookedTime?: string): string {
    if (!bookedTime) {
      return '';
    }
    const [hoursStr, minutes] = bookedTime.split(':');
    let hours = parseInt(hoursStr, 10);
    const ampm = hours >= 12 ? 'pm' : 'am';

    if (hours > 12) {
      hours -= 12;
    } else if (hours === 0) {
      hours = 12;
    }

    let formattedTime = `${hours}`;
    if (minutes !== '00') {
      formattedTime += `:${minutes}`;
    }
    formattedTime += ampm;

    return formattedTime;
  }
}

@Component({
  selector: 'claims-athome-myaccount-repair-tracker',
  templateUrl: './repair-tracker.component.html',
  styleUrls: [
    './repair-tracker.component.scss',
    './state-styles/outcard-left.scss',
  ],
})
export class RepairTrackerComponent implements OnChanges, AfterViewInit {
  @ViewChild('EngineerVisit', { static: false })
  engineerVisit: TemplateRef<TemplateContext>;

  @ViewChild('AwaitingParts', { static: false })
  awaitingParts: TemplateRef<TemplateContext>;

  @ViewChild('OutcardLeft', { static: false })
  outcardLeft: TemplateRef<TemplateContext>;

  @ViewChild('FallbackView', { static: false })
  fallbackView: TemplateRef<TemplateContext>;

  @ViewChild('NonEngineerServiceOption', { static: false })
  nonEngineerServiceOption: TemplateRef<TemplateContext>;

  @ViewChild('GenericStateView', { static: false })
  genericStateView: TemplateRef<TemplateContext>;

  @ViewChild('SingleStatusSection', { static: false })
  singleStatusSection: TemplateRef<TemplateContext>;

  @ViewChild('SelfFit', { static: false })
  selfFit: TemplateRef<TemplateContext>;

  @Input() currentStatus: string;

  @Input() claimType: ClaimType;

  @Input() appointmentHistory: MMRAppointmentHistory[] = [];

  @Input() appointmentIsToday: boolean;

  @Input() claimDetails: MMRClaimDetail;

  @Output() rebookClaim: EventEmitter<void> = new EventEmitter<void>();

  @Output() cancelClaim: EventEmitter<void> = new EventEmitter<void>();

  @Output() trackClaim: EventEmitter<void> = new EventEmitter<void>();

  @Input() isNonEngineerOption: boolean;

  @Input() showCancelButton: boolean;

  @Input() isHeatingPlan: boolean;

  @Input() isFloocareProduct: boolean;

  @Input() gadgetType: 'APPLE' | 'GADGET' | undefined;

  @Input() productCode: string;

  @Input() claimPaymentRequired: boolean | undefined;

  formattedAppointmentHistory: MMRAppointmentHistory[] = [];

  views: Record<string, TemplateRef<TemplateContext>> = {};

  incompleteStates: string[] = [];

  singleViewContent = '';

  isRemedialFlow = false;

  remedialPhoneNumber: string = REMEDIAL_WORK_PHONE_NUMBER;

  templateGenerated = [];

  nextStepsArray: string[] = [];

  courierTranslationKey = '';

  isHCFlagEnabled = false;
  flagReady = false;

  get claimLabel(): string {
    return this.claimType === 'SERVICE' ? 'service' : 'repair';
  }

  get fullTrackerEnabled(): boolean {
    const isTrackerEnabled = this.claimDetails?.isClaimsTrackerEnabled;
    const isRepair = this.claimType === 'REPAIR';

    if (!isTrackerEnabled || !isRepair) return false;

    return true;
  }

  get isPaymentPanelPresent() {
    if (this.claimType === 'SERVICE') {
      return this.claimDetails.excessPaid && this.claimPaymentRequired;
    }
    return (
      this.claimDetails?.excessPaid &&
      !!this.claimDetails?.paymentInformation?.amount &&
      this.claimDetails?.paymentInformation?.amount !== '0.00'
    );
  }

  get paymentTypeLabel(): string {
    const type = this.claimDetails?.paymentInformation?.type;
    return type === 'CALLOUT' ? 'call-out fee' : type?.toLowerCase() ?? '';
  }

  get specialStatesKeys(): string {
    const isFirstBooking = (this.claimDetails.claimStatusName || '').includes(
      '1ST'
    );

    switch (this.currentStatus) {
      case ClaimStatusTypeEnum.SparePartsNeeded:
        return '.SPARE_PARTS_NEEDED';
      case ClaimStatusTypeEnum.VisitFailed:
        return '.VISIT_FAILED.CARD_LEFT';
      case ClaimStatusTypeEnum.VisitMissed:
        return '.VISIT_MISSED.CARD_LEFT';
      case ClaimStatusTypeEnum.AppointmentRequired:
        return '.REPAIRER_WILL_ARRANGE';
      case ClaimStatusTypeEnum.NewVisitNeeded:
        return '.REPAIRER_WILL_ARRANGE';
      case ClaimStatusTypeEnum.RepairBookingNeeded:
        if (isFirstBooking) {
          return '.REPAIRER_WILL_ARRANGE_FIRST';
        }
        return '.REPAIRER_WILL_ARRANGE';
      case ClaimStatusTypeEnum.ThirdFieldCallRequired:
        return '.REPAIRER_WILL_ARRANGE';
      default:
        return '';
    }
  }

  get isRemedialWorkRequired(): boolean {
    return (
      this.currentStatus === ClaimStatusTypeEnum.RemedialWorkRequired ||
      this.currentStatus === ClaimStatusTypeEnum.RemedialWorkRequiredV2
    );
  }

  get isRemedialWorkComplete(): boolean {
    const remedialWorkCompleteStatuses: string[] = [
      ClaimStatusTypeEnum.RemedialWorkComplete,
      ClaimStatusTypeEnum.RemedialWorkARIssued,
      ClaimStatusTypeEnum.RemedialWorkIDIssued,
    ];
    return (
      this.currentStatus !== ClaimStatusTypeEnum.RemedialWorkRequired &&
      this.currentStatus !== ClaimStatusTypeEnum.RemedialWorkRequiredV2 &&
      remedialWorkCompleteStatuses.includes(
        this.claimDetails?.previousClaimStatus
      )
    );
  }

  get currentAppointmentStatus(): 'warning-state' | 'alert-state' | undefined {
    if (
      this.currentStatus !== 'Awaiting parts' &&
      this.currentStatus !== 'Spare parts ordered'
    )
      return undefined;

    const { numberOfDaysForTrackerAlert, numberOfDaysForTrackerWarning } =
      this.appConfig.runtimeFeatures;

    const appointmentDate = new Date(
      this.appointmentHistory[
        this.appointmentHistory.length - 1
      ]?.appointmentDate
    );
    const alertDate = subDays(new Date(), numberOfDaysForTrackerAlert || 5);
    const warningDate = subDays(new Date(), numberOfDaysForTrackerWarning || 3);

    if (isAfter(alertDate, appointmentDate)) return 'alert-state';

    if (isAfter(warningDate, appointmentDate)) return 'warning-state';

    return undefined;
  }

  get isWHPAndHasMobileNumber(): boolean {
    if (!this.claimDetails) return false;

    return (
      this.claimDetails.bookingOEM === 'whirlpool' &&
      this.claimDetails.customerHasMobileContactNumber
    );
  }

  get isVokeraAndHasMobileNumber(): boolean {
    if (!this.claimDetails) return false;

    return (
      this.claimDetails.bookingOEM === 'vokera' &&
      this.claimDetails.customerHasMobileContactNumber
    );
  }

  get isVokeraOem(): boolean {
    return this.claimDetails.bookingOEM === 'vokera';
  }

  get isGamesConsole(): boolean {
    return Object.keys(consoleCodes).includes(this.productCode);
  }

  get mappedStatus(): string {
    const statusMap: { [key: string]: string } = {
      'Awaiting parts': 'Waiting for parts',
      'Waiting for parts': 'Waiting for parts',
      'Spare parts ordered': 'Waiting for parts',
      'REMEDIAL WORK REQUIRED': 'Repair in progress',
      'Parts Needed': 'Spare parts needed',
      'Repair booking needed': 'Repair booking needed',
      'New visit needed': this.isVokeraOem
        ? 'New visit needed'
        : 'Repair booking needed',
      '3RD FIELD CALL REQUIRED': 'Repair in progress',
      'Safety issue': 'Safety issue',
    };
    return statusMap[this.currentStatus];
  }

  get mappedServiceOptionType(): string {
    const statusMap: { [key: string]: string } = {
      Courier: this.getCollectionLabelByProvider(),
      'Self Send': 'Send your product',
      'Drop off': 'Drop off your product',
    };
    return statusMap[this.claimDetails.serviceOptionType];
  }

  get nonEngineerNextSteps(): string[] {
    const formattedServiceOption = (this.claimDetails.serviceOptionType || '')
      .replace(' ', '_')
      .toUpperCase();
    return Array.from(
      { length: 3 },
      (_, index: number) =>
        `MMR.TRACKER.${formattedServiceOption}.STEP_${index + 1}`
    );
  }

  get titleKey(): string {
    const providerMapping: Record<string, string> = {
      [FloorcareProviderNames.NORTH_WEST_APPLIANCE]:
        'MMR.TRACKER.STEPS.NEXT_STEPS',
      [FloorcareProviderNames.EQUINOX_LTD]: 'MMR.TRACKER.STEPS.NEXT_STEPS',
    };
    const defaultProviderKey = 'MMR.TRACKER.STEPS.NEXT_STEPS_FOR_COLLECTION';

    const repairerName = this.claimDetails.repairer?.name?.toLowerCase();

    return this.isFloocareProduct
      ? providerMapping[repairerName] ?? defaultProviderKey
      : 'MMR.TRACKER.STEPS.NEXT_STEPS';
  }

  constructor(@Inject(CONFIG) public readonly appConfig: AppConfig) {}

  getCollectionLabelByProvider(): string {
    const providerName = this.claimDetails?.repairer?.name?.toLowerCase();

    const providerMapping: Record<string, string> = {
      [FloorcareProviderNames.NORTH_WEST_APPLIANCE]: 'Courier collection',
      [FloorcareProviderNames.EQUINOX_LTD]: 'Engineer collection',
    };

    const defaultFloorcareCopy = 'Parts or collection';

    if (this.gadgetType) {
      if (
        !this.claimDetails.appointmentDateSelected &&
        this.claimDetails.serviceOptionType === ServiceOptionTypeEnum.Courier
      ) {
        return 'Courier delivery';
      }
      return 'Courier collection';
    }

    if (this.isFloocareProduct) {
      return providerMapping[providerName] || defaultFloorcareCopy;
    }

    return 'Courier collection';
  }

  ngAfterViewInit(): void {
    if (
      this.currentStatus === 'Repair in progress' ||
      this.mappedStatus === 'Repair in progress'
    ) {
      posthog.capture('repair_in_progress_displayed', {
        client: 'D&G',
        status: this.claimDetails?.claimStatusName ?? '',
        repairer: this.claimDetails?.repairer?.name ?? '',
        bookingOEM: this.claimDetails.bookingOEM,
      });
    }
    this.views = {
      'Engineer visit': this.engineerVisit,
      'Awaiting parts': this.awaitingParts,
      'Waiting for parts': this.awaitingParts,
      'Spare parts ordered': this.awaitingParts,
      'Parts Needed': this.awaitingParts,
      'Please rebook': this.outcardLeft,
      'Repair in progress':
        this.claimDetails.serviceOptionType === ServiceOptionTypes.SelfFit
          ? this.selfFit
          : this.engineerVisit,
      'Spare parts needed': this.genericStateView,
      'Visit failed - to be rearranged': this.genericStateView,
      'Repair booking needed': this.isRemedialFlow
        ? this.singleStatusSection
        : this.genericStateView,
      'Appointment required': this.genericStateView,
      'New visit needed': this.genericStateView,
      '3RD FIELD CALL REQUIRED': this.genericStateView,
      'Engineer visit missed': this.outcardLeft,
      [this.claimDetails?.serviceOptionType]: this.nonEngineerServiceOption,
    };

    this.determineTemplatesForAllRecords();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.isRemedialFlow =
      !this.isWHPAndHasMobileNumber &&
      (this.isRemedialWorkComplete || this.isRemedialWorkRequired);

    if (changes.currentStatus) {
      this.singleViewContent = this.updateSingleViewTextKey();
    }

    if (changes.appointmentHistory) {
      this.formatAppointments();
    }

    if (changes.claimDetails) {
      this.nextStepsArray = this.getNextStepsArray();
      this.courierTranslationKey = this.getCourierTranslationKey();
    }
  }

  formatAppointments(): void {
    // This will be used to filter/format states at a later point!
    // TODO: Move formatting of dates etc. to the server UI shouldn't need to do this
    this.formattedAppointmentHistory = this.appointmentHistory
      .filter((item) => {
        return item.appointmentCompletionStatus !== 'Customer Request';
      })
      .map((appointment) => {
        appointment.appointmentDate = appointment.appointmentDate
          .split('/')
          .reverse()
          .join('/');

        const parsedDate = parse(
          appointment.appointmentDate,
          'yyyy/MM/dd',
          new Date()
        );
        appointment.appointmentIsToday = isToday(parsedDate);
        if (!appointment.appointmentTime || appointment.appointmentTime == '') {
          appointment.appointmentTime = undefined;
          return appointment;
        }

        appointment.appointmentTimeStart =
          appointment.appointmentTime.split(' - ')[0];
        appointment.appointmentTimeEnd =
          appointment.appointmentTime.split(' - ')[1];

        return appointment;
      });

    // If we don't want the timeline, only show the last record
    if (!this.fullTrackerEnabled && this.formattedAppointmentHistory.length) {
      this.formattedAppointmentHistory = [
        this.formattedAppointmentHistory[
          this.formattedAppointmentHistory.length - 1
        ],
      ];
    }

    if (this.formattedAppointmentHistory.length === 0) {
      this.formattedAppointmentHistory = this.claimDetails.history.slice(
        0,
        1
      ) as unknown as MMRAppointmentHistory[];
    }

    if (this.fullTrackerEnabled) {
      this.insertStateBeforeCurrentAppointment();
      this.insertStateAfterCurrentAppointment();
    }

    this.determineTemplatesForAllRecords();
  }

  determineTemplatesForAllRecords(): void {
    this.templateGenerated = this.formattedAppointmentHistory.map(
      (appointment, index) => {
        return this.determineTemplate(
          index === this.formattedAppointmentHistory.length - 1,
          appointment
        );
      }
    );
  }

  insertStateAfterCurrentAppointment(): void {
    const followUpSteps = {
      'Please rebook': ['STEPS.ENGINEER_VISIT'],
      'Awaiting parts': ['STEPS.ENGINEER_VISIT'],
      'Spare parts needed': ['STEPS.ENGINEER_VISIT'],
      'Spare parts ordered': ['STEPS.ENGINEER_VISIT'],
      'Parts Needed': ['STEPS.ENGINEER_VISIT'],
      'New visit needed': ['STEPS.ENGINEER_VISIT'],
      'Safety issue': ['STEPS.ENGINEER_VISIT'],
      'Engineer visit missed': ['STEPS.ENGINEER_VISIT'],
    };

    const statusNameFollowUpSteps = {
      'field call required': ['STEPS.ENGINEER_VISIT'],
    };

    const statusName = this.claimDetails?.claimStatusName?.toLocaleLowerCase();

    if (statusName?.includes('field call required')) {
      this.incompleteStates = statusNameFollowUpSteps['field call required'];
    } else {
      this.incompleteStates = followUpSteps[this.currentStatus] || [];
    }
  }

  insertStateBeforeCurrentAppointment(): void {
    const insertCompletedSteps = {
      'Awaiting parts': ['Engineer visit'],
      'Parts Needed': ['Engineer visit'],
      'Spare parts needed': ['Engineer visit'],
    };

    Object.keys(insertCompletedSteps).forEach((key) => {
      const index = this.formattedAppointmentHistory.findIndex(
        (item) =>
          (item.appointmentCompletionStatus || this.currentStatus) === key
      );

      if (index === -1) return;

      const item = this.formattedAppointmentHistory[index];

      const newSteps = insertCompletedSteps[key].map((value) => {
        return {
          ...item,
          appointmentCompletionStatus: value,
        };
      });

      this.formattedAppointmentHistory.splice(index, 0, ...newSteps);
    });
  }

  determineTemplate(last, item) {
    if (this.isNonEngineerOption) {
      return this.views[this.claimDetails.serviceOptionType];
    }

    if (last) {
      // Override appointment attended to show correct messaging
      if (item.appointmentCompletionStatus === 'Parts Needed') {
        this.currentStatus = 'Spare parts ordered';
        item.appointmentCompletionStatus = 'Spare parts ordered';
      }

      if (this.views[this.currentStatus]) {
        return this.views[this.currentStatus];
      } else if (!this.fullTrackerEnabled) {
        return (
          this.views[item.appointmentCompletionStatus] || this.fallbackView
        );
      }
    }

    return this.views[item?.appointmentCompletionStatus] || this.fallbackView;
  }

  get appointmentAndRepairHistory() {
    return this.formattedAppointmentHistory &&
      this.formattedAppointmentHistory.length > 0
      ? this.formattedAppointmentHistory
      : this.claimDetails.history.slice(0, 1);
  }

  private updateSingleViewTextKey(): string {
    if (!this.isRemedialFlow) {
      return '';
    }

    const statusTextMap = new Map<ClaimStatusTypeEnum, string>([
      [
        ClaimStatusTypeEnum.RepairBookingNeeded,
        'REMEDIAL_WORK_COMPLETE.STATUS',
      ],
      [
        ClaimStatusTypeEnum.RemedialWorkRequired,
        'REMEDIAL_WORK_REQUIRED.STATUS',
      ],
      [
        ClaimStatusTypeEnum.RemedialWorkRequiredV2,
        'REMEDIAL_WORK_REQUIRED.STATUS',
      ],
    ]);

    const statusKey = statusTextMap.get(
      this.currentStatus as ClaimStatusTypeEnum
    );

    return statusKey ? `MMR.SECTION.${this.claimType}.${statusKey}` : '';
  }

  getCourierTranslationKey(): string {
    const repairerDescriptionMapping: { [key: string]: string } = {
      [FloorcareProviderNames.NORTH_WEST_APPLIANCE]:
        'MMR.TRACKER.NORTH_WEST_APPLIANCE_REPAIRS_LTD.DESCRIPTION',
      [FloorcareProviderNames.EQUINOX_LTD]:
        'MMR.TRACKER.EQUINOX_MAINTENANCE_LTD.DESCRIPTION',
    };

    if (this.isFloocareProduct) {
      const repairerName = this.claimDetails.repairer?.name?.toLowerCase();
      return (
        repairerDescriptionMapping[repairerName] ||
        'MMR.TRACKER.E&S_SERVICES_COURIER.DESCRIPTION'
      );
    }

    return 'MMR.TRACKER.ENGINEER_VISIT.FUTURE.DESCRIPTION_NO_TIME';
  }

  generateFloorcareSteps(repairerName: string): string[] {
    const mapping: { [key: string]: string } = {
      [FloorcareProviderNames.NORTH_WEST_APPLIANCE]:
        'NORTH_WEST_APPLIANCE_REPAIRS_LTD',
      [FloorcareProviderNames.EQUINOX_LTD]: 'EQUINOX_MAINTENANCE_LTD',
    };

    const subKey = mapping[repairerName] || 'E&S_SERVICES_COURIER';
    const stepCount = subKey === 'E&S_SERVICES_COURIER' ? 4 : 3;

    return Array.from(
      { length: stepCount },
      (_, index) => `MMR.TRACKER.${subKey}.STEP_${index + 1}`
    );
  }

  getNextStepsArray(): string[] {
    const repairerName = this.claimDetails.repairer?.name?.toLowerCase();
    const gameReplacementStep =
      "Once your item reaches the repairer, they'll be in touch about what happens next";

    if (this.gadgetType && this.claimDetails.serviceOptionType === 'Courier') {
      if (!this.claimDetails?.appointmentDateSelected) {
        return [
          'On the day, you’ll get a text or email with your time slot (or in the DPD app)',
          'The courier will bring your replacement product within that time slot',
        ];
      }

      return [
        'On the day, you’ll get a text or email with your time slot (or in the DPD app)',
        'The courier will bring the packaging and postage labels',
        this.isGamesConsole
          ? gameReplacementStep
          : 'When your item reaches the repairer, they’ll send you confirmation and updates about the repair',
      ];
    }

    if (this.isFloocareProduct && repairerName) {
      return this.generateFloorcareSteps(repairerName);
    }

    return this.nonEngineerNextSteps;
  }
}
